import { Component, OnInit } from '@angular/core';
import { UserProfile } from 'src/app/authentication/profile/models/user-profile';
import { UserService } from 'src/app/authentication/services/user.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    PublicApiToken,
    PublicApiTokenCreateDto,
} from 'src/app/admin/models/public-api-token';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from 'src/app/shared/components/confirmation-modal/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';
import { TranslateService } from '@ngx-translate/core';
import { PublicApiTokenPage } from '../../models/public-api-token-paged-response-dto';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import {
    InputFormDialogComponent,
    InputFormDialogData,
    InputFormDialogFieldType,
} from 'src/app/shared/components/input-form-modal/input-form-dialog.component';
import { validationPatterns } from 'src/app/shared/validation-patterns';

@Component({
    selector: 'app-api-management',
    templateUrl: './api-management.component.html',
    styleUrls: ['./api-management.component.scss'],
    standalone: false,
})
export class ApiManagementComponent implements OnInit {
    hoveredDeleteButton: number = null;
    hoveredCopyToClipboardButton: number = null;
    users: UserProfile[] = [];
    isInOrg: boolean = false;

    tokens: PublicApiToken[] = [];
    tokenNameForm: FormGroup;
    groups: { [groupId: string]: string } = {};

    selectedUserId?: string;
    isLoading: boolean = false;

    // Pagination properties
    page = 1;
    pageSize = 9;
    collectionSize = 0;
    maxSize = 3;
    rotate = false;

    constructor(
        private userService: UserService,
        private dialog: MatDialog,
        private clipboard: Clipboard,
        private messageService: ToastMessageService,
        private translateService: TranslateService,
        private authenticationService: AuthenticationService
    ) {
        // Could set pageSize based on window height if ever necessary
        // this.pageSize = Math.max(1,this.pageSize - Math.ceil((1050 - window.innerHeight) / 80));
    }

    ngOnInit(): void {
        this.authenticationService.observableUserAuth().subscribe(() => {
            this.selectedUserId =
                this.authenticationService.currentUserValue.id;
            this.isInOrg =
                !!this.authenticationService.currentUserValue
                    .activeOrganization;
            if (this.isInOrg) {
                this.groups = {
                    '': this.translateService.instant(
                        'user.userArea.apiManagement.organization.noGroup'
                    ),
                    ...this.authenticationService.currentUserValue
                        .organizationProperties[
                        this.authenticationService.currentUserValue
                            .activeOrganization
                    ].groups,
                };
            }
            this.refreshTokens();
        });
    }

    refreshTokens() {
        this.isLoading = true;
        this.userService
            .getTokensPaged(this.selectedUserId, this.page - 1, this.pageSize)
            .subscribe({
                next: (tokenPage: PublicApiTokenPage) => {
                    let tokenFormPrototype = {};
                    tokenPage.content.forEach((token, index) => {
                        tokenFormPrototype['' + index] = new FormControl(
                            token.name === ''
                                ? this.translateService.instant(
                                      'user.userArea.apiManagement.defaultTokenName'
                                  )
                                : token.name,
                            {
                                validators: [
                                    Validators.pattern(
                                        validationPatterns.alphaNumericPointMinusUnderScoreBlankPattern
                                    ),
                                ],
                            }
                        );
                        if (!token.active) {
                            tokenFormPrototype['' + index].disable();
                        }
                    });
                    this.tokenNameForm = new FormGroup(tokenFormPrototype);
                    this.tokens = tokenPage.content;
                    this.collectionSize = tokenPage.totalElements;
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    // Update pagedTokens when page changes
    onPageChange(page: number) {
        this.page = page;
        this.refreshTokens();
    }

    onTokenAddButtonClick() {
        if (this.isInOrg) {
            this.openAddOrganizationTokenModal();
        } else {
            this.userService.addToken(this.selectedUserId, {}).subscribe({
                next: () => this.refreshTokens(),
                error: (error) => {
                    if (error?.error?.message === 'SUBSCRIPTION_ERROR') {
                        this.openAddTokenSubscriptionErrorDialog();
                    }
                },
            });
        }
    }

    openAddOrganizationTokenModal() {
        const data: InputFormDialogData = {
            headerTranslationKey:
                'user.userArea.apiManagement.organization.addTokenDialog.header',
            submitButtonTranslationKey:
                'user.userArea.apiManagement.organization.addTokenDialog.confirm',
            defaultErrorTranslationKey: 'default.errorMessage',
            submitAction: (dto: PublicApiTokenCreateDto) =>
                this.userService.addToken(this.selectedUserId, dto),
            inputErrorTranslationKeyPrefix:
                'user.userArea.apiManagement.errors',
            resolveErrorTranslationKey: (error: any) => {
                if (error?.error?.message === 'SUBSCRIPTION_ERROR') {
                    return 'user.userArea.apiManagement.organization.addTokenDialog.subscriptionError';
                }
                return 'default.errorMessage';
            },
            fields: [
                {
                    controlName: 'name',
                    labelTranslationKey:
                        'user.userArea.apiManagement.organization.addTokenDialog.name',
                    validators: [
                        Validators.pattern(
                            validationPatterns.alphaNumericPointMinusUnderScoreBlankPattern
                        ),
                    ],
                    maxLength: 30,
                    type: InputFormDialogFieldType.TEXT,
                },
                {
                    type: InputFormDialogFieldType.SELECT,
                    controlName: 'groupId',
                    labelTranslationKey:
                        'user.userArea.apiManagement.organization.addTokenDialog.group',
                    validators: [],
                    options: Object.entries(this.groups).map(
                        ([groupId, groupName]) => ({
                            name: groupName,
                            value: groupId,
                        })
                    ),
                },
            ],
        };
        this.dialog
            .open(InputFormDialogComponent, { data })
            .afterClosed()
            .subscribe(() => this.refreshTokens());
    }

    openAddTokenSubscriptionErrorDialog() {
        this.dialog.open<
            ConfirmationDialogComponent,
            ConfirmationDialogData,
            boolean
        >(ConfirmationDialogComponent, {
            data: {
                headerTranslationKey:
                    'user.userArea.apiManagement.subscriptionErrorDialog.addToken.header',
                confirmationQuestion: {
                    translationKey:
                        'user.userArea.apiManagement.subscriptionErrorDialog.addToken.confirmationQuestion',
                    interpolateParameters: null,
                },
                confirmButtonTranslationKey: null,
                rejectButtonTranslationKey:
                    'user.userArea.apiManagement.subscriptionErrorDialog.addToken.confirm',
                transferData: false,
            } as ConfirmationDialogData,
        });
    }

    onActiveToggleChange(token: PublicApiToken, index: number) {
        this.userService
            .activateToken(this.selectedUserId, token.id, !token.active)
            .subscribe({
                next: (updatedToken) => {
                    token.active = updatedToken.active;
                    if (!token.active) {
                        this.tokenNameForm.get('' + index).disable();
                    } else {
                        this.tokenNameForm.get('' + index).enable();
                    }
                },
                error: (error) => {
                    if (error?.error?.message === 'SUBSCRIPTION_ERROR') {
                        this.openActivateTokenSubscriptionErrorDialog();
                    }
                },
            });
    }

    openActivateTokenSubscriptionErrorDialog() {
        this.dialog.open<
            ConfirmationDialogComponent,
            ConfirmationDialogData,
            boolean
        >(ConfirmationDialogComponent, {
            data: {
                headerTranslationKey:
                    'user.userArea.apiManagement.subscriptionErrorDialog.activateToken.header',
                confirmationQuestion: {
                    translationKey: this.isInOrg
                        ? 'user.userArea.apiManagement.organization.addTokenDialog.subscriptionError'
                        : 'user.userArea.apiManagement.subscriptionErrorDialog.activateToken.confirmationQuestion',
                    interpolateParameters: null,
                },
                confirmButtonTranslationKey: null,
                rejectButtonTranslationKey:
                    'user.userArea.apiManagement.subscriptionErrorDialog.activateToken.confirm',
                transferData: false,
            } as ConfirmationDialogData,
        });
    }

    onOpenTokenDeletionDialog(token: PublicApiToken) {
        this.dialog
            .open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
                ConfirmationDialogComponent,
                {
                    data: {
                        headerTranslationKey:
                            'user.userArea.apiManagement.deleteDialog.header',
                        confirmationQuestion: {
                            translationKey:
                                'user.userArea.apiManagement.deleteDialog.question',
                            interpolateParameters: {
                                tokenName:
                                    // only display token name if set by user to != empty string
                                    token.name === ''
                                        ? ''
                                        : ' "' + token.name + '"',
                            },
                        },
                        confirmButtonTranslationKey:
                            'user.userArea.apiManagement.deleteDialog.confirm',
                        rejectButtonTranslationKey:
                            'user.userArea.apiManagement.deleteDialog.reject',
                        transferData: true,
                    } as ConfirmationDialogData,
                    width: '550px',
                }
            )
            .afterClosed()
            .subscribe((transferData) => {
                if (transferData) {
                    this.deleteToken(token);
                }
            });
    }

    deleteToken(token: PublicApiToken) {
        this.userService
            .deleteToken(this.selectedUserId, token.id)
            .subscribe(() => {
                const isLastPage =
                    this.page * this.pageSize >= this.collectionSize;

                if (this.tokens.length === 1 && this.page > 1 && isLastPage) {
                    this.page -= 1;
                }

                this.refreshTokens();
            });
    }

    onCopyToClipboard(toCopy: string) {
        this.clipboard.copy(toCopy);
        this.messageService.showSuccessMsg(
            this.translateService.instant(
                'user.userArea.apiManagement.copiedToClipboard'
            )
        );
    }

    changeTokenName(token: PublicApiToken, newName: string) {
        this.userService
            .setTokenName(this.selectedUserId, token.id, newName)
            .subscribe((updatedToken) => {
                token.name = updatedToken.name;
            });
    }

    onChangeName(event: any, token: PublicApiToken, index: number) {
        let control = this.tokenNameForm.get('' + index);
        if (control.valid) {
            if (control.value !== token.name) {
                setTimeout(() => {
                    event.target.blur();
                }, 0);
                this.changeTokenName(token, control.value);
            }
        } else {
            event.target.focus();
        }
    }

    onResetTokenName(event: any, token: PublicApiToken, index: number) {
        this.tokenNameForm.get('' + index).setValue(token.name);
        event.target.blur();
    }
}
